import { apiGet, apiPostRaw } from './api';

/** App-wide undo/redo (#278). Server: server/src/routes/history.ts. */

export interface HistoryEntry {
  id: string;
  label: string;
  source: 'ui' | 'mcp' | 'api_key' | 'calendar';
  kind: 'action' | 'typing';
  entity_type: string | null;
  entity_id: string | null;
  created_at: string;
  updated_at: string;
  undone_at: string | null;
  change_count: number;
}

export interface Blocker { id: string | null; label: string; kind: string; created_at: string | null }

export type HistoryOutcome =
  | { ok: true; id: string; label: string }
  | { ok: false; status: number; error: string; blockers: Blocker[]; actionId: string | null };

/** Fired after any undo or redo so open pages reload what changed under them. */
export const HISTORY_CHANGED_EVENT = 'dossier:history-changed';

export function listHistory(limit = 100): Promise<HistoryEntry[]> {
  return apiGet<HistoryEntry[]>(`/history?limit=${limit}`);
}

async function call(path: string): Promise<HistoryOutcome> {
  const { status, body } = await apiPostRaw<{ id?: string; label?: string; error?: string; blockers?: Blocker[]; action_id?: string | null }>(path);
  if (status === 200 && body.id) {
    window.dispatchEvent(new CustomEvent(HISTORY_CHANGED_EVENT));
    return { ok: true, id: body.id, label: body.label ?? '' };
  }
  return { ok: false, status, error: body.error ?? `Request failed (${status})`, blockers: body.blockers ?? [], actionId: body.action_id ?? null };
}

export const undo = (id: string) => call(`/history/${encodeURIComponent(id)}/undo`);
export const redo = (id: string) => call(`/history/${encodeURIComponent(id)}/redo`);
export const undoLast = () => call('/history/undo-last');
export const redoLast = () => call('/history/redo-last');

/**
 * "Undo those first": undoes the blockers newest-first, then the action, each
 * as its own checked undo. Stops at the first refusal and reports it.
 */
export async function undoWithBlockers(id: string, blockers: Blocker[]): Promise<HistoryOutcome> {
  for (const b of blockers) {
    if (!b.id) continue;
    const r = await undo(b.id);
    if (!r.ok) return r;
  }
  return undo(id);
}
